import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import SearchIcon from '@material-ui/icons/Search';
import DescriptionIcon from '@material-ui/icons/Description';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { HomeCard } from './HomeCard';
import { QuickLink } from './QuickLink';

const useStyles = makeStyles(() => ({
  linkList: {
    display: 'flex',
    flexDirection: 'column',
    margin: -8, // compensate for link padding
  },
}));

type QuickLinkItem = {
  icon: React.ReactNode;
  label: string;
  description?: string;
  href: string;
  iconBgColor?: string;
  iconColor?: string;
  external?: boolean;
};

type QuickLinksCardProps = {
  links?: QuickLinkItem[];
  isInternal?: boolean;
};

const defaultExternalLinks: QuickLinkItem[] = [
  {
    icon: <AddCircleOutlineIcon />,
    label: 'Create new project',
    description: 'Start a new development project',
    href: '/projects',
    iconBgColor: 'rgba(34, 197, 94, 0.09)',
    iconColor: '#22C55E',
  },
  {
    icon: <SearchIcon />,
    label: 'Browse catalog',
    description: 'Explore services and APIs',
    href: '/catalog',
    iconBgColor: 'rgba(82, 146, 244, 0.09)',
    iconColor: '#5292F4',
  },
  {
    icon: <DescriptionIcon />,
    label: 'View documentation',
    description: 'View technical docs',
    href: '/docs',
    iconBgColor: 'rgba(240, 163, 28, 0.09)',
    iconColor: '#F0A31C',
  },
  {
    icon: <HelpOutlineIcon />,
    label: 'Get support',
    description: 'Contact support team',
    href: '/support',
    iconBgColor: 'rgba(168, 85, 247, 0.09)',
    iconColor: '#A855F7',
  },
];

const defaultInternalLinks: QuickLinkItem[] = [
  {
    icon: <AddCircleOutlineIcon />,
    label: 'Create new project',
    description: 'Create from template',
    href: '/projects',
    iconBgColor: 'rgba(34, 197, 94, 0.09)',
    iconColor: '#22C55E',
  },
  {
    icon: <SearchIcon />,
    label: 'Browse catalog',
    description: 'Explore all entities',
    href: '/catalog',
    iconBgColor: 'rgba(82, 146, 244, 0.09)',
    iconColor: '#5292F4',
  },
  {
    icon: <DescriptionIcon />,
    label: 'View documentation',
    description: 'Technical documentation',
    href: '/docs',
    iconBgColor: 'rgba(240, 163, 28, 0.09)',
    iconColor: '#F0A31C',
  },
  {
    icon: <HelpOutlineIcon />,
    label: 'Get support',
    description: 'Contact support team',
    href: '/support',
    iconBgColor: 'rgba(168, 85, 247, 0.09)',
    iconColor: '#A855F7',
  },
];

export const QuickLinksCard = ({
  links,
  isInternal = false,
}: QuickLinksCardProps) => {
  const classes = useStyles();
  const defaultLinks = isInternal ? defaultInternalLinks : defaultExternalLinks;
  const displayLinks = links || defaultLinks;

  return (
    <HomeCard title="Quick Links">
      <Box className={classes.linkList}>
        {displayLinks.map((link, idx) => (
          <QuickLink
            key={idx}
            icon={link.icon}
            label={link.label}
            description={link.description}
            href={link.href}
            iconBgColor={link.iconBgColor}
            iconColor={link.iconColor}
            external={link.external}
          />
        ))}
      </Box>
    </HomeCard>
  );
};
